import { useState, useEffect, useCallback } from 'react'
import {
  fetchContentfulEntriesWithIncludes,
  resolveAssetUrl,
} from '../lib/contentful'

export interface StoredService {
  id: string
  title: string
  slug: string
  description: string
  image: string
  published: boolean
  order: number
  createdAt: string
}

// Contentful field shape for "services" content type
interface ContentfulServiceFields {
  serviceName: string
  slug: string
  description: string | RichTextDocument
  serviceImage?: { sys: { id: string } }
}

// Rich Text document structure from Contentful
interface RichTextDocument {
  nodeType: string
  content: RichTextNode[]
  data: Record<string, any>
}

interface RichTextNode {
  nodeType: string
  value?: string
  content?: RichTextNode[]
  marks?: { type: string }[]
  data?: Record<string, any>
}

// Extract plain text from Contentful Rich Text document
function extractPlainText(field: string | RichTextDocument): string {
  if (typeof field === 'string') return field
  if (!field || !field.content) return ''

  function walkNodes(nodes: RichTextNode[]): string {
    return nodes
      .map((node) => {
        if (node.nodeType === 'text' && node.value) return node.value
        if (node.content) return walkNodes(node.content)
        return ''
      })
      .join('')
  }

  return field.content
    .map((block) => {
      if (block.content) return walkNodes(block.content)
      return ''
    })
    .join('\n\n')
}

const STORAGE_KEY = 'belo_services'
const SERVICES_UPDATED_EVENT = 'belo_services_updated'

// ─── localStorage helpers (for admin mutations + immediate UI) ───
function getStoredServices(): StoredService[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return []
    return JSON.parse(raw)
  } catch {
    return []
  }
}

function saveServices(services: StoredService[]) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(services))
}

function dispatchServicesUpdate() {
  window.dispatchEvent(new Event(SERVICES_UPDATED_EVENT))
}

export function addService(data: Omit<StoredService, 'id'>): StoredService {
  const services = getStoredServices()
  const newService: StoredService = {
    ...data,
    id: `service_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
  }
  services.push(newService)
  saveServices(services)
  dispatchServicesUpdate()
  return newService
}

export function removeService(id: string): void {
  const services = getStoredServices().filter((s) => s.id !== id)
  saveServices(services)
  dispatchServicesUpdate()
}

export function updateService(
  id: string,
  updates: Partial<StoredService>,
): void {
  const services = getStoredServices()
  const index = services.findIndex((s) => s.id === id)
  if (index !== -1) {
    services[index] = { ...services[index], ...updates }
    saveServices(services)
    dispatchServicesUpdate()
  }
}

// ─── Map Contentful entries to StoredService shape ───
function mapContentfulService(
  entry: {
    sys: { id: string; createdAt: string }
    fields: ContentfulServiceFields
  },
  index: number,
): StoredService {
  return {
    id: entry.sys.id,
    title: entry.fields.serviceName || '',
    slug: entry.fields.slug || '',
    description: extractPlainText(entry.fields.description),
    image: '',
    published: true,
    order: index,
    createdAt: entry.sys.createdAt || new Date().toISOString(),
  }
}

// ─── Main hook ───
export function useServices() {
  const [allServices, setAllServices] = useState<StoredService[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)

  const fetchFromCMS = useCallback(async () => {
    setLoading(true)
    setError(false)
    try {
      const { items, includes } =
        await fetchContentfulEntriesWithIncludes<ContentfulServiceFields>(
          'services',
        )

      const mapped: StoredService[] = items.map((entry, index) => {
        const imageUrl = entry.fields.serviceImage
          ? resolveAssetUrl(entry.fields.serviceImage, includes)
          : ''

        return {
          id: entry.sys.id,
          title: entry.fields.serviceName || '',
          slug: entry.fields.slug || '',
          description: extractPlainText(entry.fields.description),
          image: imageUrl,
          published: true,
          order: index,
          createdAt: entry.sys.createdAt || new Date().toISOString(),
        }
      })

      console.log(
        '[Services] Mapped services:',
        mapped.map((s) => ({ title: s.title, hasImage: !!s.image })),
      )

      setAllServices(mapped)
      saveServices(mapped)
    } catch (err) {
      console.error(
        'Failed to fetch services from CMS, falling back to local:',
        err,
      )
      setError(true)
      setAllServices(getStoredServices())
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchFromCMS()
  }, [fetchFromCMS])

  // Listen for local admin mutations (optimistic updates)
  useEffect(() => {
    const handler = () => setAllServices(getStoredServices())
    window.addEventListener(SERVICES_UPDATED_EVENT, handler)
    return () => window.removeEventListener(SERVICES_UPDATED_EVENT, handler)
  }, [])

  const services = allServices
    .filter((s) => s.published)
    .sort(
      (a, b) =>
        new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
    )

  return {
    services,
    allServices: allServices.sort(
      (a, b) =>
        new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
    ),
    loading,
    error,
    refetch: fetchFromCMS,
  }
}
